<template>
  <div class="reservation-date-time-picker">
    <p class="label text-black-50 py-4">{{ $t('product_select_time') }}</p>
    <div class="datetime-selector pt-1 pb-4">
      <content-viewer :title="$t('datetime_selection_title')" modal-class="w-408px" @close="onViewerClose">
        <template #trigger="{ open }">
          <div v-gtm:click="GtmEvent.TIME_SELECTION_STARTED" class="selector-wrapper cursor-pointer" @click="handleTimeSelectorClick(open)">
            <nd-select
              v-model="bookingItemString"
              class="pointer-events-none"
              :placeholder="$t('product_select_time_place_holder')"
              :options="[]"
            ></nd-select>
          </div>
        </template>
        <template #default>
          <div
            v-if="arrivalTimes"
            class="arrival-times-root relative scrollbar max-h-[calc(100dvh-232px)] -mx-5 md:max-h-[calc(80dvh-136px)] md:-mt-4"
          >
            <div class="sticky-helper h-0 sticky top-0 z-10">
              <transition name="slide">
                <div v-show="isTimeGroupNavShown" class="group-navigator h-50px flex gap-2 w-full py-2 px-5 bg-white">
                  <template v-for="(group, i) in singleDateArrivalTimes" :key="group.time_range_name">
                    <nd-button type="secondary" size="small" :class="{ active: activatedGroupIndex === i }" @click="handleGroupNavClick(i)">
                      {{ group.time_range_name }}
                    </nd-button>
                  </template>
                </div>
              </transition>
            </div>
            <div class="py-4 px-5">
              <div v-if="isCrossDay" class="w-full flex justify-between">
                <!-- 選擇開始日期 / 選擇結束日期 -->
                <span class="text-base">{{ selectedDate[0] ? $t('datetime_selection_end_date') : $t('datetime_selection_start_date') }}</span>
                <span v-if="selectedDate[0] && selectedDate[1]" class="text-base text-yellow-50 cursor-pointer" @click="resetSelectedDate">
                  <div class="flex items-center">
                    <svg-icon name="refresh" class="w-5 h-5 inline-flex" />
                    <span class="ml-1">{{ $t('datetime_selection_reset') }}</span>
                  </div>
                </span>
              </div>
              <span v-else class="text-base">{{ $t('date_selection_title') }}</span>
            </div>
            <div ref="pickerRef" class="picker px-5">
              <date-picker-seven-day v-if="isSevenDays" v-model="selectedDate[0]" :arrival-times="arrivalTimes" />
              <date-picker-calendar
                v-else
                v-model="selectedDate"
                :arrival-times="arrivalTimes"
                :is-cross-day="isCrossDay"
                :check-out-time="props.product.checkout"
                @cross-day-item-selecting="handleCrossDayItemSelecting"
                @cross-day-item-selected="handleCrossDayItemSelected"
              />
            </div>
            <template v-if="selectedDateTimeItem">
              <hr ref="dateDividerRef" class="mx-5 mt-1 mb-1" />
              <div v-if="isCrossDay" class="py-4 px-5">
                <span class="text-base">{{ $t('time_section_choose_start_time') }}</span>
              </div>
              <div class="px-5">
                <arrival-time-selector
                  v-model="selectedDateTimeItem"
                  v-model:activated-group-index="activatedGroupIndex"
                  :selected-date="selectedDate"
                  :arrival-times="singleDateArrivalTimes"
                  :is-time-group-nav-shown="isTimeGroupNavShown"
                  :is-animating="isScrollAnimating"
                  :is-cross-day="isCrossDay"
                  :cross-day-items="crossDayItems"
                  :check-out-time="props.product.checkout"
                  @cross-day-sell-time-selected="handleCrossDaySellTimeSelected"
                />
              </div>
            </template>
          </div>
        </template>
        <template #footer="{ close }">
          <div class="footer w-full">
            <arrival-time-remain-hint
              :today-remain-max="singleDateRemainMaxCount"
              :remain-mode="arrivalTimes?.remain?.mode"
              :remain-reference="arrivalTimes?.remain?.reference"
              :remain-count="selectedDateTimeItem?.remain_count"
            />
            <arrival-time-price-bar
              :is-cross-day="isCrossDay"
              :check-out-time="props.product.checkout"
              :currency-code="getCurrencyCode(arrivalTimes?.currency_id)"
              :booking-item="tmpBookingItem"
              :timezone="arrivalTimes?.timezone"
              @submit="handleArrivalTimeSelect(close)"
            />
          </div>
        </template>
      </content-viewer>
    </div>
  </div>
</template>

<script setup lang="ts">
import { compareAsc, format, parseISO } from 'date-fns'

import ContentViewer from '@/components/common/content-viewer.vue'
import DatePickerSevenDay from '@/components/reservation/date-picker-seven-day.vue'
import DatePickerCalendar from '@/components/reservation/date-picker-calendar.vue'
import ArrivalTimeSelector from '@/components/reservation/arrival-time-selector.vue'
import ArrivalTimeRemainHint from '@/components/reservation/arrival-time-remain-hint.vue'
import ArrivalTimePriceBar from '@/components/reservation/arrival-time-price-bar.vue'

import { getBranchArrivalTimes } from '@/service/branch'

import { EdbProductTimeMode as ApiEdbProductTimeMode } from '@/types/api'
import type { SselldateSelltime } from '@/types/api'
import type { UiProductInfo, UiProductSelectedData } from '@/types/product'
import { GtmEvent } from '@/types/gtm'
import { CurrencyEnum } from '@/types/filters'

const bookingItem = defineModel<UiProductSelectedData[]>({ default: [] })
const props = defineProps<{
  product: UiProductInfo
}>()

const { regionId } = useRegion()

const selectedDate = ref<[string, string]>(['', ''])
const selectedDateTimeItem = ref<Nullable<SselldateSelltime>>(null)
const crossDayItems = ref<UiProductSelectedData[]>([])
const tmpBookingItem = ref<UiProductSelectedData[]>([])

const activatedGroupIndex = ref(0)
const isScrollAnimating = ref(false)
const isTimeGroupNavShown = ref(false)

const pickerRef = ref<Nullable<HTMLElement>>(null)
const dateDividerRef = ref<Nullable<HTMLElement>>(null)

const {
  data: arrivalTimes,
  pending: isFetchingBranchArrivalTimes,
  refresh: refetchBranchArrivalTimes,
} = await useLazyAsyncData(async () => {
  if (!props.product?.bid) return
  const queryParams = { sale_region_id: regionId.value, ...(props.product.pid && { pid: props.product.pid }) }
  const { data } = await getBranchArrivalTimes(props.product.bid, queryParams)
  return data
})

const isSevenDays = computed(() => arrivalTimes.value?.time_mode === ApiEdbProductTimeMode.ProductTimeModeDefault)
const isCrossDay = computed(() => !!props.product.is_crossday)

const singleDateArrivalTimes = computed(() => {
  if (!arrivalTimes.value?.selldates) return []
  return arrivalTimes.value.selldates[selectedDate.value[0]] ?? []
})

const singleDateRemainMaxCount = computed(() => {
  if (!arrivalTimes.value?.selldates) return 0

  const remainCountArray = singleDateArrivalTimes.value.map(date => date.selltimes?.map(time => time.remain_count || 0) || 0).flat()
  return Math.max(...remainCountArray)
})

const bookingItemString = computed(() => {
  const startDate = bookingItem.value?.at?.(0)
  // 如果沒有選擇時間，則顯示「請選擇時間」
  // 如果使用空字串，Naive UI 的 select 會顯示空白，故回傳 'undefined'
  if (!startDate?.dateTime) return undefined

  let result = format(parseISO(startDate.dateTime), 'yyyy/MM/dd HH:mm')

  // 處理跨日商品沒跨日的情況
  if (isCrossDay.value && bookingItem.value.length === 1) {
    const [startDateString] = startDate.dateTime.split(' ')
    result = `${result} ~ ${format(parseISO(`${startDateString} ${props.product.checkout}`), 'yyyy/MM/dd HH:mm')}`
  } else {
    const endDate = bookingItem.value?.at?.(-1)
    if (endDate && endDate !== startDate) result += ` ~ ${format(parseISO(endDate.dateTime), 'yyyy/MM/dd HH:mm')}`
  }
  return result
})

const sellTimesBySellDate = computed(() => {
  if (!arrivalTimes.value?.selldates) return {}

  return Object.entries(arrivalTimes.value.selldates).reduce<Record<string, (SselldateSelltime | undefined)[]>>((acc, [date, sellDates]) => {
    acc[date] = sellDates.map(sellDate => sellDate.selltimes).flat()

    return acc
  }, {})
})

const setSelectedDateDefaultValue = () => {
  if (arrivalTimes.value?.selldates) {
    const sortedDates = Object.entries(arrivalTimes.value.selldates).sort((a, b) => compareAsc(parseISO(a[0]), parseISO(b[0])))
    const [defaultSelectedDate, dates] = sortedDates?.[0] || []
    const defaultSelectedDateTimeItem = dates?.[0]?.selltimes?.[0]
    if (isSevenDays.value && defaultSelectedDate && defaultSelectedDateTimeItem) {
      selectedDate.value = [defaultSelectedDate, '']
      selectedDateTimeItem.value = defaultSelectedDateTimeItem
    } else {
      selectedDate.value = ['', '']
      selectedDateTimeItem.value = null
      tmpBookingItem.value = []
    }
  }
}

const handleTimeSelectorClick = async (cb: Function) => {
  if (isFetchingBranchArrivalTimes.value) return

  if (bookingItem.value.length > 0) {
    tmpBookingItem.value = cloneDeep(bookingItem.value)
  }

  if (selectedDateTimeItem.value) return cb()

  await refetchBranchArrivalTimes()

  setSelectedDateDefaultValue()

  cb()
}

const handleArrivalTimeSelect = (cb: Function) => {
  if (!selectedDateTimeItem.value?.selltime || !tmpBookingItem.value[0]) return
  bookingItem.value = cloneDeep(tmpBookingItem.value)

  useNuxtApp().$trackEvent({
    event: GtmEvent.TIME_SELECTION_COMPLETED,
    payload: {
      'Booking Time': tmpBookingItem.value[0].dateTime,
    },
  })
  cb()
}

const handleCrossDayItemSelecting = () => {
  tmpBookingItem.value = []
}

const handleCrossDayItemSelected = (item: UiProductSelectedData[]) => {
  crossDayItems.value = cloneDeep(item)
}

const handleCrossDaySellTimeSelected = (item: UiProductSelectedData[]) => {
  tmpBookingItem.value = cloneDeep(item)
}

const handleGroupNavClick = (index: number) => {
  const id = `group_${index}`
  scrollToId(id)
  activatedGroupIndex.value = index
  isScrollAnimating.value = true
  debounceResetAnimating()
}

const getCurrencyCode = (id = 0) => CurrencyEnum[id]

const debounceResetAnimating = useDebounceFn(() => {
  isScrollAnimating.value = false
}, 1500)

const restoreSelectedDateTimeItem = (sellTime: string) => {
  const [date = ''] = sellTime.split(' ')
  const foundDateItem = sellTimesBySellDate.value[date]?.find?.(item => item?.selltime === sellTime)

  if (foundDateItem) {
    selectedDateTimeItem.value = foundDateItem
  }
}

const restoreSelectedDate = () => {
  if (bookingItem.value.length === 0) return setSelectedDateDefaultValue()

  const dateTime = bookingItem.value.map(item => item.dateTime)
  const startDateTime = dateTime.at(0) || ''
  const endDateTime = dateTime.at(-1) || ''
  const [startDate = ''] = startDateTime.split(' ')
  const [endDate = ''] = endDateTime.split(' ')

  selectedDate.value = [startDate, endDate]

  /**
   * 因 `selectedDateTimeItem` 會在 `selectedDate` 變化時，將 `selectedDateTimeItem` 改成該日期的第一個時間，
   * 故需要在下一個循環後再恢復 `selectedDateTimeItem`。
   */
  nextTick(() => {
    restoreSelectedDateTimeItem(startDateTime)
  })
}

const onViewerClose = () => {
  restoreSelectedDate()
}

onMounted(() => {
  useIntersectionObserver(
    pickerRef,
    entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) {
          isTimeGroupNavShown.value = true
        } else {
          isTimeGroupNavShown.value = false
        }
      })
    },
    {
      threshold: [0],
    }
  )
})

// 切換選擇的日期時，時間重設為該日期的第一個時間
watch(
  selectedDate,
  (newVal, oldVal) => {
    if (newVal[0] === oldVal[0] && newVal[1] === oldVal[1]) return
    const firstItem = arrivalTimes.value?.selldates?.[selectedDate.value[0]]?.[0]?.selltimes?.[0] || null

    selectedDateTimeItem.value = firstItem
    activatedGroupIndex.value = 0
  },
  { deep: true }
)
// 切換選擇日期或打開彈窗時，滾動到日期分隔線，讓使用者看到時間區塊
watchDebounced(
  [selectedDate, dateDividerRef],
  ([newSelectedDate, newDateDividerRef]) => {
    if (!newSelectedDate[0]) return
    newDateDividerRef?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  },
  {
    debounce: 300,
  }
)

watch(
  selectedDateTimeItem,
  () => {
    if (!selectedDateTimeItem.value) return
    tmpBookingItem.value[0] = {
      dateTime: selectedDateTimeItem.value.selltime || '',
      price: selectedDateTimeItem.value.price || 0,
      originPrice: selectedDateTimeItem.value.origin_price || 0,
    }
  },
  { deep: true }
)

function resetSelectedDate() {
  selectedDate.value = ['', '']
  selectedDateTimeItem.value = null
  tmpBookingItem.value = []
  nextTick(() => {
    // v-calendar selecting range date 的時候，reset 後，內部資料跟 selectedDate 不同步，要再清一次，UI 才會正確顯示
    selectedDate.value = ['', '']
  })
}
</script>

<style lang="scss" scoped></style>
